// frontend/src/pages/ResetPassword.js
import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { useNavigate } from "react-router-dom";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabaseがトークンを自動処理するのを待つ
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("セッション確認エラー:", error);
          setError("無効なリンクです。パスワードリセットを再度申請してください。");
          return;
        }

        if (session) {
          console.log("✅ セッション確立:", session);
          setSessionReady(true);
        } else {
          // セッションがない場合、少し待ってから再確認
          setTimeout(checkSession, 500);
        }
      } catch (err) {
        console.error("セッションチェックエラー:", err);
        setError("エラーが発生しました。");
      }
    };

    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // パスワードの確認
    if (password !== confirmPassword) {
      setError("パスワードが一致しません");
      return;
    }

    if (password.length < 8) {
      setError("パスワードは8文字以上にしてください");
      return;
    }

    setLoading(true);

    try {
      console.log("🔄 パスワード更新開始...");
      
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error("❌ パスワード更新エラー:", error);
        throw error;
      }

      console.log("✅ パスワード更新成功:", data);
      setSuccess(true);

      // 3秒後にログイン画面へ
      setTimeout(() => {
        navigate("/login");
      }, 3000);

    } catch (error) {
      console.error("❌ パスワード更新エラー:", error);
      
      if (error.message.includes("session")) {
        setError("セッションの有効期限が切れています。パスワードリセットを再度申請してください。");
      } else {
        setError("パスワードの更新に失敗しました。もう一度お試しください。");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              パスワードを変更しました
            </h2>
            <p className="text-gray-600 mb-6">
              新しいパスワードでログインできます。
              3秒後にログイン画面に移動します...
            </p>
            <button
              onClick={() => navigate("/login")}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-6 rounded-lg transition"
            >
              今すぐログイン画面へ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // セッション確立を待つローディング表示
  if (!sessionReady && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              準備中...
            </h2>
            <p className="text-gray-600">
              パスワードリセットの準備をしています
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            新しいパスワードを設定
          </h1>
          <p className="text-gray-600">
            新しいパスワードを入力してください
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
            {error.includes("無効なリンク") || error.includes("有効期限") ? (
              <button
                onClick={() => navigate("/forgot-password")}
                className="mt-3 text-sm text-red-700 hover:text-red-800 underline"
              >
                パスワードリセットを再申請する
              </button>
            ) : null}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              新しいパスワード
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="8文字以上"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              新しいパスワード（確認）
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="8文字以上"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            }`}
          >
            {loading ? "更新中..." : "パスワードを変更"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;